import React, { useCallback, useEffect, useState } from "react"
import styles from "./chatBots.module.css"
import { ReactFlow, Controls, Background, applyNodeChanges, applyEdgeChanges, addEdge, Panel, useReactFlow, MiniMap } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import Aside from "./Aside"
import Button from '../../../components/Form/Button'
import AiConditional from "../../../components/Flow/Nodes/AiConditional"
import Default from "../../../components/Flow/Nodes/Default"
import End from "../../../components/Flow/Nodes/End"
import Start from "../../../components/Flow/Nodes/Start"
import ModalFlow from "../../../components/Modal/Flow/ModalFlow"
import Loading from "../../layouts/Loading"
import api from "../../../services/api"
import { toast } from "react-toastify"
import { useParams } from "react-router-dom"
import FormGroup from './../../../components/Form/FormGroup';

const initialNodes = [
    { id: '1', type: 'start', data: { label: 'Conversa Iniciada' }, position: { x: 0, y: 0 } },
]

const nodeTypes = { start: Start, aiConditional: AiConditional, aiInstructions: Default, aiResponse: Default, closeConversation: End, transferService: End }

const ChatBotPreview = () => {
    const [nodes, setNodes] = useState(initialNodes)
    const [edges, setEdges] = useState([])
    const [selectedNode, setSelectedNode] = useState()
    const [showModal, setShowModal] = useState(false)
    const [pendingSave, setPendingSave] = useState(false)
    const [active, setActive] = useState(false)
    const [copiedNode, setCopiedNode] = useState(null)
    const [loading, setLoading] = useState(false)

    const { flow_id } = useParams()
    const { screenToFlowPosition } = useReactFlow()

    useEffect(() => {
        if (flow_id) {
            getDataByFlow(flow_id)
        }
    }, [flow_id])

    useEffect(() => {
        setPendingSave(true)
    }, [nodes, edges])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!showModal) {
                const isCtrl = event.ctrlKey || event.metaKey

                if (isCtrl && event.key === 'c') {
                    const selected = nodes.find(n => n.selected && n.id !== '1')
                    if (selected) {
                        setCopiedNode(selected)
                    }
                }

                if (isCtrl && event.key === 'v' && copiedNode) {
                    const newId = `${Date.now()}`
                    const newNode = {
                        ...copiedNode,
                        id: newId,
                        position: {
                            x: copiedNode.position.x + 40,
                            y: copiedNode.position.y + 40,
                        },
                        selected: false
                    }
                    setNodes((nds) => [...nds, newNode])
                    setCopiedNode(null)
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [copiedNode, nodes, showModal])

    const onNodesChange = useCallback(
        (changes) => {
            const updatedChanges = changes.filter(change => {
                if (change.type === 'remove') {
                    return change.id !== '1'
                }
                return true
            })
            setNodes((nds) => applyNodeChanges(updatedChanges, nds))
        }, []
    )

    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    )

    const onConnect = useCallback(
        (connection) => {
            const alreadyConnected = edges.some(edge =>
                edge.source === connection.source &&
                edge.sourceHandle === connection.sourceHandle
            )

            if (alreadyConnected) {
                toast.warning("Este ponto de saída já está conectado!")
                return
            }

            setEdges((eds) => addEdge(connection, eds))
        },
        [edges]
    )

    const onDrop = useCallback((event) => {
        event.preventDefault()

        const node = JSON.parse(event.dataTransfer.getData("application/reactflow"))
        if (!node) {
            return
        }

        const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        })
        const newNode = {
            ...node,
            id: `${new Date().getTime()}`,
            position,
        }

        setNodes((nds) => nds.concat(newNode))
    }, [nodes, screenToFlowPosition])

    const onDragOver = (event) => {
        event.preventDefault()
    }

    const onNodeDoubleClick = (event, node) => {
        if (node.id === '1') {
            return
        }
        setSelectedNode(node)
        handleOpenModal()
    }

    const handleUpdateContentByNode = (nodeId, newContent) => {
        setNodes((nds) => {
            return nds.map((node) =>
                node.id === nodeId ? { ...node, data: { ...node.data, content: newContent } } : node
            )
        })
    }

    const handleOpenModal = () => {
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedNode()
    }

    const getDataByFlow = (flow_id) => {
        setLoading(true)
        api.get(`/flows/chatbots/${flow_id}`).then(response => {
            const flow = response.data.data || {}
            if (flow.nodes?.length) {
                setNodes(flow.nodes)
            }
            setEdges(flow.edges || [])
            setActive(flow.status === "ACTIVE")
        }).catch(err => console.log(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const handleSaveFlow = () => {
        setLoading(true)
        api.patch(`/flows/chatbots/${flow_id}`, { nodes, edges }).then(response => {
            toast.success(response.data.message)
        }).catch(err => console.log(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const handleStatusFlow = () => {
        setLoading(true)
        api.patch(`/flows/chatbots/${flow_id}/status`, { status: active ? "DRAFT" : "ACTIVE" }).then(response => {
            setActive(previousValue => !previousValue)
            toast.success(response.data.message)
        }).catch(err => console.log(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    return (
        <div className={styles.wrapper}>
            <Aside />
            <div className={styles.container} onDrop={onDrop} onDragOver={onDragOver}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeDoubleClick={onNodeDoubleClick}
                    nodeTypes={nodeTypes}
                    proOptions={{ hideAttribution: true }}
                    fitView
                    connectionMode="loose"
                >
                    <Background />
                    <Controls />
                    <MiniMap />
                    <Panel position="top-right">
                        <FormGroup>
                            <Button variant="primary" onClick={handleSaveFlow} disabled={!pendingSave}>Salvar Alterações</Button>
                            <Button variant="secondary" onClick={handleStatusFlow}>{active ? "Voltar para rascunho" : "Ativar fluxo"}</Button>
                        </FormGroup>
                    </Panel>
                </ReactFlow>
            </div>
            {showModal &&
                <ModalFlow
                    node={selectedNode}
                    onSubmit={handleUpdateContentByNode}
                    closeModal={handleCloseModal}
                />
            }
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default ChatBotPreview
